import { useQuery } from '@tanstack/react-query';
import {
  sesionApi,
  sesionKeys,
  elegirProcesoActivo,
  elegirSesionActiva,
  type MiParticipacion,
  type ProcesoResumen,
} from '../lib/sesion';
import { leerSala } from '../lib/sala';
import { useAuthStore } from '../store/authStore';

/**
 * Resuelve la sesión "en curso" del usuario. Poll suave (5 s) porque la
 * estructura del proceso cambia poco durante una sesión; lo que late rápido
 * son los aportes, no la agenda.
 *
 * ORDEN DE RESOLUCIÓN del proceso (bug real del owner: entró por QR al Sprint
 * CCJ con Google y el Bolsillo lo llevó al demo del agua):
 *   1. El proceso ELEGIDO explícitamente (p. ej. /cabina?proceso=<id>).
 *   2. EL CÓDIGO MANDA: la sala por la que se entró (lib/sala). Es la puerta
 *      que la persona acaba de usar; le gana a cualquier heurística. Sin esto,
 *      con dos membresías se aporta evidencia en la sala equivocada y no se
 *      entera nadie. Manda para el proceso Y para el día: la sesión que nombró
 *      el QR se respeta mientras no esté cerrada (ver `elegirSesionActiva`).
 *   3. La membresía: sus participaciones (activas primero — el backend ya las
 *      ordena así). Un usuario Google nuevo cae en la org default, pero su
 *      membresía dice a qué proceso pertenece de verdad.
 *   4. SOLO SUPERADMIN: el primer proceso activo de la org.
 *
 * Por qué la regla 4 se limitó al superadmin (jornada CCJ del 6-ago): tres
 * personas entraron con Google sin pasar por el QR, quedaron sin ninguna
 * membresía, y esta regla las mandó al primer proceso activo de la org por
 * defecto — un CLON de prueba del proceso real. El backend hizo lo correcto y
 * les negó el WebSocket 359 veces; ellas solo vieron una sala muerta.
 *
 * El backend deja pasar sin membresía a UNA sola clase de usuario: el
 * superadmin (`exigir_miembro`, `_autorizar` del WS). Elegir un proceso donde
 * la persona no es miembro no es un fallback: es mandarla a una pared. Sin
 * membresía y sin código, no hay sala — y el Bolsillo pide el código.
 */
export interface SesionActiva {
  procesoActivo: ProcesoResumen | null;
  sesionId: string | null;
  cargando: boolean;
  error: Error | null;
}

export function useSesionActiva(
  procesoId?: string | null,
  /** Día elegido a mano en la Cabina (?sesion=): manda sobre la heurística. */
  sesionElegidaId?: string | null,
): SesionActiva {
  const procesos = useQuery({
    queryKey: sesionKeys.procesos,
    queryFn: sesionApi.procesos,
    refetchInterval: 5000,
  });

  const participaciones = useQuery({
    queryKey: ['mis-participaciones'],
    queryFn: sesionApi.misParticipaciones,
    refetchInterval: 30_000,
  });

  // 1 · Elegido explícito (si el id no aparece en la lista, se ignora).
  const elegido = procesoId
    ? (procesos.data?.find((p) => p.id === procesoId) ?? null)
    : null;

  /** Participación -> ProcesoResumen (mismo shape para todas las vías). */
  const deParticipacion = (p: MiParticipacion | undefined): ProcesoResumen | null =>
    p
      ? {
          id: p.proceso_id,
          nombre: p.proceso_nombre,
          estado: p.proceso_estado,
          problema_enunciado: null,
          org_nombre: p.org_nombre,
        }
      : null;

  // 2 · El código manda: la sala por la que se entró. Si ya cargaron las
  //     participaciones, el nombre sale de ahí (el retorno de Google no lo
  //     trae); si no, alcanza con lo que guardó el login por sala.
  const sala = leerSala();
  const porSala: ProcesoResumen | null = sala
    ? (deParticipacion(
        participaciones.data?.find((p) => p.proceso_id === sala.proceso_id),
      ) ?? {
        id: sala.proceso_id,
        nombre: sala.proceso_nombre || 'Tu sala',
        estado: 'activo',
        problema_enunciado: null,
        org_nombre: sala.org_nombre || null,
      })
    : null;

  // 3 · La membresía: primera participación (activas primero).
  const porMembresia = deParticipacion(participaciones.data?.[0]);

  // 4 · Solo superadmin: es el único que el backend autoriza sin membresía.
  //     Para el resto, un proceso sin membresía es una pared (403 en el WS y
  //     en cada aporte), así que preferimos NINGÚN proceso y pedir el código.
  const esSuperadmin = useAuthStore((s) => s.user?.rol) === 'superadmin';
  const porOrg =
    esSuperadmin && procesos.data ? elegirProcesoActivo(procesos.data) : null;

  const procesoActivo = elegido ?? porSala ?? porMembresia ?? porOrg;

  const proceso = useQuery({
    queryKey: procesoActivo ? sesionKeys.proceso(procesoActivo.id) : ['proceso', 'nada'],
    queryFn: () => sesionApi.proceso(procesoActivo!.id),
    enabled: Boolean(procesoActivo),
    refetchInterval: 5000,
  });

  // EL CÓDIGO MANDA TAMBIÉN PARA EL DÍA: si estamos en el proceso que abrió el
  // QR, la sesión que ese código nombró le gana a la heurística. Solo aplica
  // cuando el proceso coincide — si se eligió otro (p. ej. /cabina?proceso=),
  // el puntero de la sala no tiene nada que decir sobre sus días.
  const sesionDeSala =
    sala && procesoActivo && sala.proceso_id === procesoActivo.id ? sala.sesion_id : null;

  const sesion = proceso.data
    ? elegirSesionActiva(proceso.data, {
        elegidaId: sesionElegidaId,
        ancladaId: sesionDeSala,
      })
    : null;

  return {
    procesoActivo,
    sesionId: sesion?.id ?? null,
    cargando:
      procesos.isLoading ||
      participaciones.isLoading ||
      (Boolean(procesoActivo) && proceso.isLoading),
    error: (procesos.error as Error | null) ?? (proceso.error as Error | null),
  };
}
